import React from "react";
import { motion } from "framer-motion";
import { Handshake, Share2, Cpu, Users, Landmark, Gift, BadgeCheck, Headphones, Rocket, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Section, Container, Eyebrow, RevealHeading, Reveal, TextLink } from "@/components/primitives";
import { PageHero } from "@/components/PageHero";
import { CTASection } from "@/components/CTASection";
import { Seo } from "@/components/Seo";
import { IMAGES } from "@/data/site";

const PARTNER_TYPES = [
  { icon: Share2, title: "Referral Partners", body: "Introduce us to a government, candidate or institution that needs delivery help. We do the work; you share in the success." },
  { icon: Handshake, title: "Delivery Partners", body: "Firms and consultancies that deliver alongside GovX on larger engagements — bringing complementary skills." },
  { icon: Cpu, title: "Technology Partners", body: "Build with, integrate or resell GovX Digital products — delivery, citizen, political and intelligence platforms." },
  { icon: Users, title: "Advisors & Associates", body: "Senior experts — former officials, strategists, engineers — who work with GovX on specific mandates." },
  { icon: Landmark, title: "Development Partners", body: "Donors and programmes co-funding public impact, monitoring and citizen-focused work across Nigeria." },
];

const OFFERINGS = [
  { title: "Government", to: "/government", refer: "Delivery units, PMOs, M&E, citizen experience and governance reform." },
  { title: "Political", to: "/political", refer: "Strategy, organisation, campaign operations and transition support." },
  { title: "Digital", to: "/digital", refer: "Delivery, citizen, political and intelligence platforms." },
  { title: "Impact & Intelligence", to: "/impact", refer: "Research, monitoring, analytics and the GovX Delivery Index." },
];

const STEPS = [
  { n: "01", title: "Introduce", body: "Tell us about yourself and the kind of work or clients you can connect us with." },
  { n: "02", title: "Onboard", body: "We agree simple terms — referral, delivery or technology — and how we work together." },
  { n: "03", title: "Enable", body: "You get the materials and briefings to explain GovX clearly to clients." },
  { n: "04", title: "Refer or Deliver", body: "You refer opportunities or deliver alongside us on live engagements." },
  { n: "05", title: "Share Success", body: "You earn referral rewards or delivery revenue as the work moves forward." },
];

const BENEFITS = [
  { icon: Gift, title: "Referral Rewards", body: "Clear rewards when an introduction becomes a live engagement." },
  { icon: BadgeCheck, title: "Co-Branded Materials", body: "Decks and one-pagers you can share with confidence." },
  { icon: Headphones, title: "Deal Support", body: "We join your client conversations and help scope the work." },
  { icon: Rocket, title: "Priority Access", body: "Early access to GovX products, reports and the Delivery Index." },
];

const IDEAL = ["Consulting firms", "Technology companies", "Communications agencies", "Industry associations", "Individual advisors", "Development organisations"];

export default function PartnerWithUs() {
  return (
    <>
      <Seo
        title="Partner With Us | GovX Nigeria"
        description="Partner with GovX Nigeria — refer clients, deliver alongside us or resell GovX Digital products. Clear offerings and rewards for referral and cross-selling partners."
      />
      <PageHero
        eyebrow="Partnerships"
        title={["Grow With GovX.", "Deliver With GovX."]}
        copy="We work with partners who can open doors, deliver alongside us or bring technology to the table. If you know an institution, leader or programme that needs to get something done — let's work together."
        cta="Become a Partner"
        image={IMAGES.proMacbook}
        kicker="Referral · Delivery · Technology"
      />

      {/* Partnership types */}
      <Section className="py-24 lg:py-32">
        <Container>
          <Eyebrow>Ways To Partner</Eyebrow>
          <RevealHeading
            lines={["Five Simple Ways", "To Work With Us."]}
            as="h2"
            className="mt-7 font-display text-4xl font-medium leading-[1.02] tracking-tight sm:text-5xl"
          />
          <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {PARTNER_TYPES.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
                className="group border border-govx-charcoal/12 bg-white p-8 transition-colors duration-500 hover:border-govx-green"
              >
                <p.icon className="h-7 w-7 text-govx-green" strokeWidth={1.5} />
                <h3 className="mt-6 font-display text-xl font-medium tracking-tight">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-govx-charcoal/65">{p.body}</p>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* What you can offer clients */}
      <Section dark className="py-24 lg:py-32">
        <Container>
          <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr]">
            <div>
              <Eyebrow dark>What You Can Offer Clients</Eyebrow>
              <RevealHeading
                lines={["A Complete", "GovX Offering", "To Refer Or Resell."]}
                as="h2"
                className="mt-7 font-display text-3xl font-medium leading-[1.05] tracking-tight sm:text-4xl"
              />
              <Reveal delay={0.1}>
                <p className="mt-6 max-w-md text-base leading-relaxed text-govx-grey">
                  Whatever your client needs — a stalled programme fixed, an organisation built, a
                  platform shipped or outcomes measured — there is a clear GovX offering you can put
                  in front of them.
                </p>
              </Reveal>
            </div>
            <div className="grid gap-px overflow-hidden border border-white/10 bg-white/5 sm:grid-cols-2">
              {OFFERINGS.map((o, i) => (
                <motion.div
                  key={o.title}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="bg-govx-black p-7"
                >
                  <div className="font-mono text-xs text-govx-lime">{String(i + 1).padStart(2, "0")}</div>
                  <h3 className="mt-5 font-display text-xl font-medium tracking-tight text-govx-paper">{o.title}</h3>
                  <p className="mt-3 text-sm text-govx-grey">{o.refer}</p>
                  <Link to={o.to} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-govx-paper transition-colors hover:text-govx-lime">
                    See offering <ArrowRight className="h-4 w-4" />
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* How it works */}
      <Section className="py-24 lg:py-32">
        <Container>
          <Eyebrow>How Partnering Works</Eyebrow>
          <RevealHeading
            lines={["Simple. Clear.", "No Surprises."]}
            as="h2"
            className="mt-7 font-display text-3xl font-medium tracking-tight sm:text-5xl"
          />
          <div className="mt-14 grid gap-px overflow-hidden border border-govx-charcoal/10 bg-govx-charcoal/10 md:grid-cols-5">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: i * 0.1 }}
                className="group bg-govx-paper p-7 transition-colors duration-500 hover:bg-govx-charcoal hover:text-govx-paper"
              >
                <div className="font-mono text-4xl font-light text-govx-grey group-hover:text-govx-lime">{s.n}</div>
                <h3 className="mt-6 font-display text-lg font-medium tracking-tight">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-govx-charcoal/60 group-hover:text-govx-paper/70">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Benefits + ideal partners */}
      <Section dark className="py-24 lg:py-32">
        <Container>
          <div className="grid gap-14 lg:grid-cols-[1.1fr_0.9fr]">
            <div>
              <Eyebrow dark>What You Get</Eyebrow>
              <RevealHeading
                lines={["Partner Benefits."]}
                as="h2"
                className="mt-7 font-display text-3xl font-medium tracking-tight sm:text-4xl"
              />
              <div className="mt-10 grid gap-5 sm:grid-cols-2">
                {BENEFITS.map((b, i) => (
                  <motion.div
                    key={b.title}
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    className="border border-white/10 bg-govx-charcoal/40 p-6"
                  >
                    <b.icon className="h-6 w-6 text-govx-lime" strokeWidth={1.5} />
                    <h3 className="mt-5 font-display text-lg font-medium text-govx-paper">{b.title}</h3>
                    <p className="mt-2 text-sm text-govx-grey">{b.body}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            <div>
              <Eyebrow dark>Ideal Partners</Eyebrow>
              <div className="mt-8 space-y-0 border-t border-white/10">
                {IDEAL.map((it, i) => (
                  <Reveal key={it} delay={i * 0.05}>
                    <div className="flex items-center gap-4 border-b border-white/10 py-4">
                      <span className="font-mono text-xs text-govx-green">{String(i + 1).padStart(2, "0")}</span>
                      <span className="font-display text-lg font-medium tracking-tight text-govx-paper">{it}</span>
                    </div>
                  </Reveal>
                ))}
              </div>
              <Reveal delay={0.2}>
                <div className="mt-8">
                  <TextLink to="/contact" dark>Talk to the partnerships team</TextLink>
                </div>
              </Reveal>
            </div>
          </div>
        </Container>
      </Section>

      <CTASection
        eyebrow="Partnerships"
        title={["Let's build public", "impact together."]}
        copy="Tell us how you'd like to partner — refer, deliver or bring technology — and we'll take it from there."
        cta="Become a Partner"
      />
    </>
  );
}
